import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'; 
console.log('🔧 API_BASE_URL:', API_BASE_URL); 

// Create axios instance
const api = axios.create({       
  baseURL: API_BASE_URL,   
  headers: {
    'Content-Type': 'application/json',   
  },
}); 

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('🌐 Making API request to:', (config.baseURL || '') + (config.url || ''));
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use( 
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: { name: string; email: string; password: string; phone?: string }) =>
    api.post('/auth/register', data),
  
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  getProfile: () => api.get('/auth/profile'),
  
  updateProfile: (data: any) => api.put('/auth/profile', data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  
  logout: () => api.post('/auth/logout'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  
  getAdminStats: () => api.get('/dashboard/admin/stats'),
  
  getUsers: (params?: any) => api.get('/dashboard/admin/users', { params }),
  
  getRecentActivity: (limit?: number) =>
    api.get('/dashboard/admin/recent-activity', { params: { limit } }),
  
  getPayoutStats: (period?: number) =>
    api.get('/dashboard/payouts/stats', { params: { period } }),
  
  getRecentPayouts: (limit?: number) =>
    api.get('/dashboard/payouts/recent', { params: { limit } }),
  
  getMyActivity: () => api.get('/dashboard/my-activity'),
};

// Payout API
export const payoutAPI = {
  initiate: (data: any) => api.post('/viyon/payouts/initiate', data),
  
  getStatus: (merchantRef: string) =>
    api.get(`/viyon/payouts/status/${merchantRef}`),
  
  getAll: (params?: any) => api.get('/viyon/payouts', { params }),
  
  getById: (id: number) => api.get(`/viyon/payouts/${id}`),
  
  getStats: () => api.get('/viyon/payouts/stats'),
};

// Public API (no authentication required)
export const publicAPI = {
  getMaintenanceStatus: () => api.get('/maintenance-status'),
};

// Admin API
export const adminAPI = {
  // Statistics & Dashboard
  getStats: () => api.get('/admin/stats'),
  getDashboardOverview: () => api.get('/admin/dashboard-overview'),
  
  getReports: (period?: number) => api.get('/admin/reports', { params: { period } }),
  
  getActivityLogs: (params?: any) => api.get('/admin/activity-logs', { params }),
  
  // User Management
  getAllUsers: (params?: any) => api.get('/admin/users', { params }),
  
  getUserById: (userId: number) => api.get(`/admin/users/${userId}`),
  
  createUser: (data: any) => api.post('/admin/users', data),
  
  updateUser: (userId: number, data: any) => api.put(`/admin/users/${userId}`, data),
  
  deleteUser: (userId: number) => api.delete(`/admin/users/${userId}`),
  
  toggleUserStatus: (userId: number, is_active: boolean) =>
    api.patch(`/admin/users/${userId}/toggle-status`, { is_active }),
  
  resetUserPassword: (userId: number, new_password: string) =>
    api.post(`/admin/users/${userId}/reset-password`, { new_password }),
  
  getUserRoles: () => api.get('/admin/roles'),
  
  // Transaction Management
  getAllTransactions: (params?: any) => api.get('/admin/transactions', { params }),
  
  getTransactions: (params?: any) => api.get('/admin/transactions', { params }),
  
  // Payout Management
  getAllPayouts: (params?: any) => api.get('/admin/payouts', { params }),
  listBlacklistedPayouts: () => api.get('/admin/fraud/blacklisted-payouts'),
  addBlacklistedPayout: (data: {
    account_name?: string;
    bank_name: string;
    account_number?: string;
    ifsc_code: string;
    reason?: string;
    ifsc_only?: boolean;
  }) => api.post('/admin/fraud/blacklisted-payouts', data),
  removeBlacklistedPayout: (id: string) => api.delete(`/admin/fraud/blacklisted-payouts/${id}`),
  listPayoutErrorLogs: (params?: { merchant_id?: string; trace_id?: string; stage?: string; limit?: number }) =>
    api.get('/admin/fraud/payout-error-logs', { params }),

  // Merchant Refunds (refund transactions from failed payouts)
  getMerchantRefunds: (params?: any) => api.get('/admin/refunds', { params }),
  
  getPayoutDetails: (merchantReferenceNumber: string) => 
    api.get(`/admin/payouts/${merchantReferenceNumber}`),
  
  checkPayoutStatus: (merchantReferenceNumber: string) => 
    api.get(`/admin/payouts/${merchantReferenceNumber}/status`),

  /** PoolPay live enquiry (admin). Only for payouts with payout_source POOLPAY. */
  checkPoolPayPayoutStatus: (merchantReferenceNumber: string) =>
    api.get(`/admin/payouts/${encodeURIComponent(merchantReferenceNumber)}/poolpay-status`),
  
  /** Status Checker: lookup credentials by merchant reference to auto-fill form */
  getPayoutCredentialsForStatusChecker: (merchantReference: string) =>
    api.get(`/admin/payouts/status-checker-lookup/${encodeURIComponent(merchantReference)}`),

  /** Status Checker: check payout status with provided credentials; returns full Viyona response */
  checkPayoutStatusWithCredentials: (data: {
    client_id: string;
    client_secret: string;
    api_key: string;
    merchant_reference_number: string;
    transaction_date: string;
  }) => api.post('/admin/payouts/check-status-with-credentials', data),

  // System Settings
  getSystemSettings: () => api.get('/admin/settings'),
  
  updateSystemSettings: (settings: any) => api.put('/admin/settings', settings),

  // Drop default JSON Content-Type so the browser sets multipart/form-data with a boundary.
  // (Explicit "multipart/form-data" without boundary breaks multer on production.)
  uploadAppApk: (formData: FormData) =>
    api.post('/admin/settings/app/apk-upload', formData, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      // axios: `false` removes default JSON Content-Type for this request
      headers: { 'Content-Type': false } as import('axios').RawAxiosRequestHeaders,
    }),

  getAppApkVersions: () => api.get('/admin/settings/app/apk-versions'),
  
  // Emergency Actions
  emergencyAction: (action: string) => api.post('/admin/emergency-action', { action }),
  
  // Admin Wallet
  getAdminWallet: () => api.get('/admin/wallet'),
  
  transferToUser: (data: { toUserId: number; amount: number; description: string }) =>
    api.post('/admin/wallet/transfer', data),
  
  getUsersForTransfer: () => api.get('/admin/wallet/users'),
  
  generateCoins: () => api.post('/admin/wallet/generate-coins'),
  
  getUsers: () => api.get('/admin/users'),
  
  // CRON Logs
  getCronLogs: (params?: any) => api.get('/admin/cron/logs', { params }),

  // Backup
  getBackupSettings: () => api.get('/admin/backup/settings'),
  updateBackupSettings: (data: { zip_password?: string }) => api.put('/admin/backup/settings', data),
  getBackupTables: () => api.get('/admin/backup/tables'),
  createBackup: (data: { tables?: string[]; password?: string; use_stored_password?: boolean }) =>
    api.post('/admin/backup/create', data),
  listBackups: () => api.get('/admin/backup/list'),
  downloadBackup: (filename: string) =>
    api.get(`/admin/backup/download/${filename}`, { responseType: 'blob' }),
  
  // Use the same endpoint as merchant dashboard for consistent ViyonaPay responses
  checkPayoutStatusMerchant: (merchantReferenceNumber: string) => 
    api.get(`/merchants/payout-status/${merchantReferenceNumber}`),
};

export default api;




